import { useMemo, useState } from "react";
import { Check, ChevronRight, Phone } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type OpportunityStatus =
  | "opportunity"
  | "ready"
  | "approaching"
  | "quote_sent"
  | "negotiation"
  | "signing"
  | "won"
  | "lost";

type BlockState = "empty" | "pending" | "done";

type SubStatus = {
  key: string;
  label: string;
  done: boolean;
  highlightWhenDone?: boolean;
};

type BlockData = {
  key: string;
  label: string;
  state: BlockState;
  date: Date | null;
  hint?: string;
  subStatuses?: SubStatus[];
};

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function fmtDate(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return null;
  return DATE_FMT.format(date);
}

function toDate(d: Date | string | null | undefined): Date | null {
  if (!d) return null;
  const date = typeof d === "string" ? new Date(d) : d;
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateInputValue(d: Date | null): string {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function OpportunityStatusBar({
  opportunityId,
  currentStatus,
  createdAt,
  briefGeneratedAt,
  callAt,
  selectedProducts,
  selectedProductsAt,
}: {
  opportunityId: string;
  currentStatus: OpportunityStatus;
  createdAt: Date | string;
  briefGeneratedAt: Date | string | null;
  callAt: Date | string | null;
  selectedProducts: ReadonlyArray<unknown> | null;
  selectedProductsAt: Date | string | null;
}) {
  const utils = trpc.useUtils();
  const proposalsList = trpc.proposals.list.useQuery({ opportunityId });
  const signingList = trpc.signing.list.useQuery({ opportunityId });
  const history = trpc.opportunities.statusHistory.useQuery({ opportunityId });
  const trackingEvents = trpc.opportunities.trackingEvents.useQuery({ opportunityId });

  const updateOpp = trpc.opportunities.update.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: opportunityId });
    },
    onError: (e) => toast.error(e.message ?? "Failed to update call date"),
  });

  const [callPopoverOpen, setCallPopoverOpen] = useState(false);
  const callDate = toDate(callAt);
  const [callInput, setCallInput] = useState(toDateInputValue(callDate));

  const wonDate = useMemo(() => {
    if (currentStatus !== "won") return null;
    const entry = (history.data ?? []).find((h) => h.toStatus === "won");
    return entry ? toDate(entry.changedAt as unknown as Date) : null;
  }, [history.data, currentStatus]);

  // Earliest "quote was sent or beyond" transition in the audit log — used
  // as a fallback when no proposal record carries a sentAt yet (status was
  // moved manually, or the quote went out via channels other than the
  // built-in proposals.send flow).
  const quoteSentHistoryDate = useMemo(() => {
    const QUOTE_SENT_STATES = new Set([
      "quote_sent",
      "negotiation",
      "signing",
      "won",
    ]);
    const entry = (history.data ?? []).find((h) =>
      QUOTE_SENT_STATES.has(h.toStatus)
    );
    return entry ? toDate(entry.changedAt as unknown as Date) : null;
  }, [history.data]);

  const blocks = useMemo<BlockData[]>(() => {
    const now = new Date();

    // Opportunity: green when brief content has been generated
    const briefDate = toDate(briefGeneratedAt);
    const opportunityBlock: BlockData = {
      key: "opportunity",
      label: "Opportunity",
      state: briefDate ? "done" : "pending",
      date: briefDate ?? toDate(createdAt),
    };

    // Call: grey when callAt is in the future, green once it has passed
    let callState: BlockState = "empty";
    if (callDate) {
      callState = callDate.getTime() <= now.getTime() ? "done" : "pending";
    }
    const callBlock: BlockData = {
      key: "call",
      label: "Call",
      state: callState,
      date: callDate,
      hint:
        callState === "empty"
          ? "Set call date"
          : callState === "pending"
            ? "Call scheduled"
            : "Call passed",
    };

    // Products: green when at least one selected product
    const hasProducts = (selectedProducts ?? []).length > 0;
    const productsBlock: BlockData = {
      key: "products",
      label: "Products",
      state: hasProducts ? "done" : "empty",
      date: hasProducts ? toDate(selectedProductsAt) : null,
    };

    // Quote: grey when any proposal has been sent, green when any accepted.
    // Falls back to the opportunity's own status (and the status-history
    // entry that recorded the transition) when no proposal carries a
    // sentAt — e.g. the quote went out as a manual email and the user
    // moved the pipeline pill themselves.
    const proposals = proposalsList.data ?? [];
    const acceptedProposal = proposals.find((p) => p.status === "accepted");
    const sentProposal =
      acceptedProposal ??
      proposals.find(
        (p) => p.status === "sent" || p.status === "viewed" || p.status === "rejected"
      );
    const statusImpliesQuoteSent =
      currentStatus === "quote_sent" ||
      currentStatus === "negotiation" ||
      currentStatus === "signing" ||
      currentStatus === "won";
    let quoteState: BlockState = "empty";
    let quoteDate: Date | null = null;
    if (acceptedProposal) {
      quoteState = "done";
      quoteDate = toDate(acceptedProposal.acceptedAt) ?? toDate(acceptedProposal.sentAt);
    } else if (sentProposal) {
      quoteState = "pending";
      quoteDate = toDate(sentProposal.sentAt);
    } else if (statusImpliesQuoteSent) {
      quoteState = "pending";
      quoteDate = quoteSentHistoryDate;
    }
    const events = trackingEvents.data ?? [];
    const emailOpened = events.some(
      (e) => e.eventType === "email_opened" || e.eventType === "email_unique_opened"
    );
    const landingOpened = proposals.some((p) => p.firstViewedAt != null);
    const quoteApproved = !!acceptedProposal;
    // Show only the most-progressed sub-status that has actually fired,
    // ordered earliest → latest in the funnel. Empty when nothing has
    // happened yet.
    const SUB_STEPS: ReadonlyArray<SubStatus> = [
      { key: "email_opened", label: "Email opened", done: emailOpened },
      { key: "landing_opened", label: "Landing opened", done: landingOpened },
      {
        key: "quote_approved",
        label: "Quote approved",
        done: quoteApproved,
        highlightWhenDone: true,
      },
    ];
    const latestActive = [...SUB_STEPS].reverse().find((s) => s.done);
    const quoteBlock: BlockData = {
      key: "quote",
      label: "Quote",
      state: quoteState,
      date: quoteDate,
      subStatuses:
        quoteState === "empty" || !latestActive ? undefined : [latestActive],
    };

    // Agreement: grey when any signing request sent, green when completed
    const signing = signingList.data ?? [];
    const completedSigning = signing.find((s) => s.status === "completed");
    const sentSigning =
      completedSigning ??
      signing.find(
        (s) =>
          s.status === "sent" ||
          s.status === "partially_signed" ||
          s.status === "archived"
      );
    let agreementState: BlockState = "empty";
    let agreementDate: Date | null = null;
    if (completedSigning) {
      agreementState = "done";
      agreementDate =
        toDate(completedSigning.completedAt) ?? toDate(completedSigning.sentAt);
    } else if (sentSigning) {
      agreementState = "pending";
      agreementDate = toDate(sentSigning.sentAt);
    }
    const agreementBlock: BlockData = {
      key: "agreement",
      label: "Agreement",
      state: agreementState,
      date: agreementDate,
    };

    // Won: green when status === "won"
    const wonBlock: BlockData = {
      key: "won",
      label: "Won",
      state: currentStatus === "won" ? "done" : "empty",
      date: wonDate,
    };

    return [
      opportunityBlock,
      callBlock,
      productsBlock,
      quoteBlock,
      agreementBlock,
      wonBlock,
    ];
  }, [
    briefGeneratedAt,
    callDate,
    createdAt,
    selectedProducts,
    selectedProductsAt,
    proposalsList.data,
    signingList.data,
    trackingEvents.data,
    currentStatus,
    wonDate,
    quoteSentHistoryDate,
  ]);

  const handleSaveCall = () => {
    const value = callInput.trim();
    const next = value ? new Date(`${value}T12:00:00`) : null;
    if (value && (!next || Number.isNaN(next.getTime()))) {
      toast.error("Invalid date");
      return;
    }
    updateOpp.mutate(
      { id: opportunityId, patch: { callAt: next ? next.toISOString() : null } },
      {
        onSuccess: () => {
          setCallPopoverOpen(false);
        },
      }
    );
  };

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="-mx-4 flex items-start gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        {blocks.map((b, i) => {
          const isCall = b.key === "call";
          const isWonHighlighted = b.key === "won" && b.state === "done";

          const content = (
            <div
              className={cn(
                "flex flex-col gap-0.5 rounded-md px-3 py-2 text-center transition-colors",
                isWonHighlighted
                  ? "bg-black text-white"
                  : b.state === "done"
                    ? "bg-emerald-100 text-emerald-900"
                    : b.state === "pending"
                      ? "bg-slate-200 text-slate-800"
                      : "bg-muted text-muted-foreground",
                isCall && "cursor-pointer hover:opacity-90"
              )}
            >
              <span className="inline-flex items-center justify-center gap-1 text-xs font-semibold leading-tight">
                {b.state === "done" ? (
                  <Check className="h-3 w-3 shrink-0" />
                ) : b.state === "pending" ? (
                  <Check className="h-3 w-3 shrink-0 opacity-60" />
                ) : isCall ? (
                  <Phone className="h-3 w-3 shrink-0 opacity-60" />
                ) : null}
                {b.label}
              </span>
              <span className="text-[10px] leading-tight opacity-80">
                {b.date ? fmtDate(b.date) : b.hint ?? "—"}
              </span>
            </div>
          );

          return (
            <div
              key={b.key}
              className="flex shrink-0 basis-[120px] flex-col gap-1 sm:flex-1 sm:basis-0"
            >
              <div className="flex items-center gap-1.5">
                {isCall ? (
                  <Popover
                    open={callPopoverOpen}
                    onOpenChange={(open) => {
                      setCallPopoverOpen(open);
                      if (open) setCallInput(toDateInputValue(callDate));
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button type="button" className="flex-1 text-left">
                        {content}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 space-y-3" align="center">
                      <div className="space-y-1">
                        <label
                          htmlFor="call-at-input"
                          className="text-xs font-medium text-muted-foreground"
                        >
                          Call date
                        </label>
                        <Input
                          id="call-at-input"
                          type="date"
                          value={callInput}
                          onChange={(e) => setCallInput(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCallInput("");
                            updateOpp.mutate(
                              { id: opportunityId, patch: { callAt: null } },
                              {
                                onSuccess: () => setCallPopoverOpen(false),
                              }
                            );
                          }}
                          disabled={updateOpp.isPending || !callDate}
                        >
                          Clear
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveCall}
                          disabled={updateOpp.isPending}
                        >
                          Save
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex-1">{content}</div>
                )}
                {i < blocks.length - 1 && (
                  <ChevronRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                )}
              </div>
              {b.subStatuses && b.subStatuses.length > 0 && (
                <ul className="flex flex-col gap-0.5 px-1">
                  {b.subStatuses.map((s) => {
                    const isGreen = s.done && s.highlightWhenDone;
                    return (
                      <li
                        key={s.key}
                        className={cn(
                          "inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] leading-tight",
                          isGreen
                            ? "bg-emerald-200 text-emerald-900"
                            : s.done
                              ? "text-slate-700"
                              : "text-muted-foreground/60"
                        )}
                      >
                        <Check
                          className={cn(
                            "h-2.5 w-2.5 shrink-0",
                            !s.done && "opacity-30"
                          )}
                        />
                        <span className="truncate">{s.label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
